import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "./apiRequest";
import { showToast } from "./toastOptions";

/**
 * Hook encapsulating notification fetch, append, and mark-read logic.
 *
 * @param {Object} opts
 * @param {boolean} opts.isAdmin
 * @param {boolean} opts.ready - true when user session is loaded and ready
 * @returns notification state + handlers
 */
export function useNotifications({ isAdmin, ready }) {
  const [notifications, setNotifications] = useState([]);
  const notificationsRef = useRef(notifications);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  const fetchNotifications = useCallback(async () => {
    if (!isAdmin) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    try {
      const json = await apiRequest(`${process.env.REACT_APP_API_BASE_URL}/notifications/`, {
        method: "GET",
        suppressErrorToast: true,
      });
      const data = json?.notifications ?? json?.data ?? [];
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast("error", err.message || "Failed to fetch notifications");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  // Fetch once when ready
  useEffect(() => {
    if (ready) fetchNotifications();
  }, [ready, fetchNotifications]);

  const handleNotificationNew = useCallback((data) => {
    if (!data || data.id == null) return;
    const parsedCompanyId = Number(data.company_id);
    const companyId = Number.isFinite(parsedCompanyId) ? parsedCompanyId : data.company_id;
    const newItem = { ...data, company_id: companyId };
    setNotifications((prev) => {
      const first = prev[0];
      if (first && (first.timestamp || "") >= (data.timestamp || "")) {
        return prev;
      }
      const updated = [newItem, ...prev.filter((n) => n.id !== data.id)];
      return updated.slice(0, 200);
    });
  }, []);

  const markNotificationsRead = useCallback(async (notificationIds = null) => {
    try {
      let ids = [];
      if (notificationIds === null) {
        ids = [];
      } else if (Array.isArray(notificationIds)) {
        ids = notificationIds;
      } else {
        ids = [notificationIds];
      }

      const prev = notificationsRef.current;
      const toMark = ids.length === 0 ? prev.map((n) => n.id) : ids;
      const byCompany = {};
      prev.forEach((n) => {
        if (toMark.includes(n.id) && n.company_id != null) {
          const cid = String(n.company_id);
          if (!byCompany[cid]) byCompany[cid] = [];
          byCompany[cid].push(n.id);
        }
      });
      const companyIds = Object.keys(byCompany);
      if (companyIds.length > 0) {
        await Promise.allSettled(
          companyIds.map((cid) =>
            apiRequest(
              `${process.env.REACT_APP_API_BASE_URL}/notifications/company/${cid}/mark-read/`,
              {
                method: "POST",
                body: { notification_ids: byCompany[cid] },
                suppressErrorToast: true,
              }
            )
          )
        );
      }

      setNotifications((prevState) => {
        if (ids.length === 0) {
          return prevState.map((n) => ({ ...n, is_read: true }));
        }
        return prevState.map((n) => (ids.includes(n.id) ? { ...n, is_read: true } : n));
      });
    } catch (err) {
      showToast("error", err.message || "Failed to mark notifications as read");
    }
  }, []);

  return {
    notifications,
    loading,
    fetchNotifications,
    handleNotificationNew,
    markNotificationsRead,
  };
}
